/**
 * Admin authentication.
 *
 * Being signed in is not the same as being an admin: `admin` is a custom claim
 * minted by scripts/set-admin-claim.mjs and carried in the ID token. Firestore
 * rules check that claim on every write, so this context only decides what the
 * panel renders — it is not the security boundary.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut as fbSignOut,
    sendPasswordResetEmail,
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from "firebase/auth";
import { auth } from "@/lib/firebase";

const AuthContext = createContext(null);

/** Firebase's error codes are not fit to show a human. */
function friendlyAuthError(code, fallback) {
    switch (code) {
        case "auth/invalid-email":
            return "That email address is not valid.";
        case "auth/user-disabled":
            return "This account has been disabled.";
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
            // Deliberately vague: distinguishing the two would let anyone probe
            // which email addresses have accounts.
            return "Incorrect email or password.";
        case "auth/too-many-requests":
            return "Too many attempts. Wait a few minutes and try again.";
        case "auth/network-request-failed":
            return "Network error. Check your connection and try again.";
        case "auth/configuration-not-found":
            return "Email/password sign-in is not enabled on this Firebase project yet.";
        default:
            return fallback || "Could not sign in. Please try again.";
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth) {
            setLoading(false);
            return undefined;
        }

        return onAuthStateChanged(auth, async (nextUser) => {
            if (!nextUser) {
                setUser(null);
                setIsAdmin(false);
                setLoading(false);
                return;
            }

            try {
                // force-refresh: a claim granted after this session started is
                // absent from the cached token, so without this a freshly
                // promoted admin would keep being told they lack access.
                const token = await nextUser.getIdTokenResult(true);
                setIsAdmin(token.claims.admin === true);
            } catch (err) {
                console.error("[auth] could not read custom claims:", err);
                setIsAdmin(false);
            }

            setUser(nextUser);
            setLoading(false);
        });
    }, []);

    const signIn = useCallback(async (email, password) => {
        if (!auth) throw new Error("Firebase is not configured. Check frontend/.env.");
        try {
            const cred = await signInWithEmailAndPassword(auth, email, password);
            const token = await cred.user.getIdTokenResult(true);
            if (token.claims.admin !== true) {
                // Authenticated but unauthorised. Drop the session rather than
                // leave them signed in with no access.
                await fbSignOut(auth);
                throw new Error(
                    "This account does not have admin access. Ask an administrator to run: npm run set-admin -- --email " +
                        email,
                );
            }
            return cred.user;
        } catch (err) {
            if (err.code) throw new Error(friendlyAuthError(err.code, err.message));
            throw err;
        }
    }, []);

    const signOut = useCallback(async () => {
        if (auth) await fbSignOut(auth);
    }, []);

    const resetPassword = useCallback(async (email) => {
        if (!auth) throw new Error("Firebase is not configured.");
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (err) {
            throw new Error(friendlyAuthError(err.code, err.message));
        }
    }, []);

    /**
     * Change the signed-in admin's password with no email round-trip.
     *
     * This is the reset path that actually works here: the site runs no mailbox
     * on its domain, so an emailed reset link (resetPassword) has nowhere to
     * land. Firebase requires a fresh credential before a password change, so
     * the current password is re-verified first — which also stops someone
     * changing the password on an unattended, already-signed-in session.
     */
    const changePassword = useCallback(async (currentPassword, newPassword) => {
        if (!auth?.currentUser) throw new Error("You are not signed in.");
        if ((newPassword || "").length < 6) {
            throw new Error("New password must be at least 6 characters.");
        }
        try {
            const credential = EmailAuthProvider.credential(
                auth.currentUser.email,
                currentPassword,
            );
            await reauthenticateWithCredential(auth.currentUser, credential);
            await updatePassword(auth.currentUser, newPassword);
        } catch (err) {
            if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
                throw new Error("Your current password is incorrect.");
            }
            throw new Error(friendlyAuthError(err.code, err.message));
        }
    }, []);

    const value = useMemo(
        () => ({ user, isAdmin, loading, signIn, signOut, resetPassword, changePassword }),
        [user, isAdmin, loading, signIn, signOut, resetPassword, changePassword],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>.");
    return ctx;
}
